import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Code,
  Heading,
  HStack,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { RefreshCw } from 'lucide-react';
import {
  ALERT_THRESHOLD_LABELS,
  type AlertState,
  type ClientErrorGroup,
  type HealthCheckRow,
  type HealthWindow,
  type PushFailureReason,
  type ServerErrorGroup,
  type SweepStatus,
} from '@twinden/shared';
import {
  adminApi,
  ApiError,
  type HealthErrors,
  type HealthOverview,
} from '../../lib/api';
import { appLocale } from '../../lib/format';

// The admin panel's Health tab (M67, docs/monitoring.md D256): what the
// canary found (D253), request and error rates, push, the scheduler and
// the sweeps, error groups (D254) and the alert state per threshold (D255).
// Nothing here names a person: error groups carry route patterns and
// scrubbed messages.

const WINDOW_LABELS: Record<HealthWindow, string> = {
  '24h': '24 h',
  '7d': '7 d',
};

export const CHECK_LABELS: Record<string, string> = {
  db: 'Database',
  media: 'Media signing',
  websocket: 'WebSocket',
  storage: 'Storage',
  scheduler: 'Scheduler',
  sweeps: 'Sweeps',
  push: 'Push',
};

export const pct = (value: number | null, digits = 1) =>
  value === null ? '—' : `${(value * 100).toFixed(digits)}%`;

export function ago(iso: string | null, now = Date.now()): string {
  if (!iso) return 'never';
  const seconds = Math.max(
    0,
    Math.round((now - new Date(iso).getTime()) / 1000)
  );
  if (seconds < 60) return `${seconds} s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

export function duration(seconds: number): string {
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 48 * 3600) return `${(seconds / 3600).toFixed(1)} h`;
  return `${Math.round(seconds / 86400)} d`;
}

const every = (ms: number) =>
  ms >= 3600_000
    ? `${Math.round(ms / 3600_000)} h`
    : `${Math.round(ms / 60_000)} min`;

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(appLocale()) : '—';

function Card({
  label,
  value,
  note,
  tone = 'neutral',
  testId,
  children,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'neutral' | 'good' | 'bad' | 'warn';
  testId?: string;
  children?: ReactNode;
}) {
  const color =
    tone === 'good'
      ? 'green.600'
      : tone === 'bad'
        ? 'red.600'
        : tone === 'warn'
          ? 'orange.600'
          : 'ink';
  return (
    <Box
      bg="surface"
      borderWidth="1px"
      borderColor="line"
      borderRadius="card"
      p={4}
      data-testid={testId}
    >
      <Text
        fontSize="xs"
        color="muted"
        textTransform="uppercase"
        letterSpacing="wide"
      >
        {label}
      </Text>
      <Text
        fontSize="26px"
        fontWeight={800}
        color={color}
        lineHeight={1.2}
        mt={1}
      >
        {value}
      </Text>
      {note && (
        <Text fontSize="xs" color="muted" mt={1} noOfLines={2}>
          {note}
        </Text>
      )}
      {children}
    </Box>
  );
}

const TRANSPORT_LABELS: Record<PushFailureReason['transport'], string> = {
  vapid: 'Web',
  apns: 'iOS',
  fcm: 'Android',
};

/**
 * Why pushes failed in the last hour (issue #70), e.g. "Android:
 * SENDER_ID_MISMATCH ×6", with what to check for the common mistakes.
 */
function PushFailures({
  failures,
  configProblems,
}: {
  failures: PushFailureReason[];
  configProblems: string[];
}) {
  const hints = [
    ...new Set([
      ...configProblems,
      ...failures
        .map((failure) => failure.hint)
        .filter((hint): hint is string => hint !== null),
    ]),
  ];
  if (failures.length === 0 && hints.length === 0) return null;
  return (
    <VStack align="stretch" spacing={0.5} mt={2}>
      {failures.slice(0, 6).map((failure) => (
        <Text
          key={`${failure.transport} ${failure.reason}`}
          fontSize="xs"
          color="red.600"
          data-testid="push-failure"
          wordBreak="break-word"
        >
          {TRANSPORT_LABELS[failure.transport]}:{' '}
          <Code fontSize="xs">{failure.reason}</Code> ×{failure.count}
        </Text>
      ))}
      {hints.map((hint) => (
        <Text
          key={hint}
          fontSize="xs"
          color="muted"
          data-testid="push-hint"
          wordBreak="break-word"
        >
          {hint}
        </Text>
      ))}
    </VStack>
  );
}

function OkBadge({ ok, label }: { ok: boolean | null; label?: string }) {
  return (
    <Badge
      colorScheme={ok === null ? 'gray' : ok ? 'green' : 'red'}
      borderRadius="full"
      px={2}
      textTransform="none"
    >
      {label ?? (ok === null ? 'no data' : ok ? 'ok' : 'failed')}
    </Badge>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Heading size="sm" mb={2}>
        {title}
      </Heading>
      {children}
    </Box>
  );
}

function CanaryChecks({ latest }: { latest: HealthCheckRow[] }) {
  if (latest.length === 0) {
    return <Text color="muted">The canary hasn&apos;t run yet.</Text>;
  }
  return (
    <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={2}>
      {latest.map((check) => (
        <HStack
          key={check.name}
          justify="space-between"
          bg="surface"
          borderWidth="1px"
          borderColor="line"
          borderRadius="xl"
          px={3}
          py={2}
          data-testid="canary-check"
        >
          <Box minW={0}>
            <Text fontSize="sm" fontWeight={600}>
              {CHECK_LABELS[check.name] ?? check.name}
            </Text>
            <Text
              fontSize="xs"
              color="muted"
              noOfLines={1}
              title={check.reason ?? ''}
            >
              {check.reason ?? (check.ms !== null ? `${check.ms} ms` : '')}
            </Text>
          </Box>
          <OkBadge ok={check.ok} />
        </HStack>
      ))}
    </SimpleGrid>
  );
}

function Sweeps({ sweeps }: { sweeps: SweepStatus[] }) {
  if (sweeps.length === 0)
    return <Text color="muted">No sweeps registered.</Text>;
  return (
    <VStack align="stretch" spacing={1}>
      {sweeps.map((sweep) => (
        <HStack
          key={sweep.label}
          justify="space-between"
          fontSize="sm"
          data-testid="sweep"
        >
          <Text noOfLines={1}>
            {sweep.label}{' '}
            <Text as="span" color="muted">
              · every {every(sweep.everyMs)}
            </Text>
          </Text>
          <HStack spacing={2} flexShrink={0}>
            <Text color="muted" fontSize="xs" title={sweep.lastRunAt ?? ''}>
              {sweep.running ? 'running' : ago(sweep.lastRunAt)}
            </Text>
            {sweep.lastError ? (
              <OkBadge ok={false} label="failed" />
            ) : (
              <OkBadge
                ok={!sweep.overdue}
                label={sweep.overdue ? 'overdue' : 'ok'}
              />
            )}
          </HStack>
        </HStack>
      ))}
    </VStack>
  );
}

function Stack({ stack }: { stack: string | null }) {
  const [open, setOpen] = useState(false);
  if (!stack)
    return (
      <Text color="muted" fontSize="xs">
        no stack
      </Text>
    );
  return (
    <Box>
      <Button
        size="xs"
        variant="link"
        colorScheme="gray"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide stack' : 'Show stack'}
      </Button>
      {open && (
        <Code
          display="block"
          whiteSpace="pre-wrap"
          fontSize="xs"
          mt={1}
          p={2}
          borderRadius="md"
          maxH="240px"
          overflowY="auto"
          data-testid="error-stack"
        >
          {stack}
        </Code>
      )}
    </Box>
  );
}

function ServerErrors({ groups }: { groups: ServerErrorGroup[] }) {
  if (groups.length === 0)
    return <Text color="muted">No server errors in this window.</Text>;
  return (
    <VStack align="stretch" spacing={2}>
      {groups.map((group) => (
        <Box
          key={group.id}
          bg="surface"
          borderWidth="1px"
          borderColor="line"
          borderRadius="card"
          px={4}
          py={3}
          data-testid="server-error"
        >
          <HStack
            justify="space-between"
            align="flex-start"
            flexWrap="wrap"
            spacing={2}
          >
            <Box minW={0}>
              <Text
                fontSize="sm"
                fontWeight={700}
                fontFamily="mono"
                wordBreak="break-all"
              >
                {group.method} {group.route} → {group.status}
              </Text>
              <Text fontSize="sm" mt={1} wordBreak="break-word">
                {group.message}
              </Text>
            </Box>
            <HStack spacing={2} flexShrink={0}>
              <Badge borderRadius="full" px={2} textTransform="none">
                {group.count}×
              </Badge>
              <Text fontSize="xs" color="muted" title={group.lastSeen}>
                {ago(group.lastSeen)}
              </Text>
            </HStack>
          </HStack>
          <HStack
            mt={2}
            spacing={3}
            fontSize="xs"
            color="muted"
            flexWrap="wrap"
          >
            <Text>first {when(group.firstSeen)}</Text>
            {group.build && <Text>build {group.build.slice(0, 7)}</Text>}
            <Text fontFamily="mono">{group.fingerprint.slice(0, 10)}</Text>
          </HStack>
          <Box mt={1}>
            <Stack stack={group.stack} />
          </Box>
        </Box>
      ))}
    </VStack>
  );
}

function ClientErrors({ groups }: { groups: ClientErrorGroup[] }) {
  if (groups.length === 0)
    return <Text color="muted">No app errors in this window.</Text>;
  return (
    <VStack align="stretch" spacing={2}>
      {groups.map((group) => (
        <Box
          key={group.id}
          bg="surface"
          borderWidth="1px"
          borderColor="line"
          borderRadius="card"
          px={4}
          py={3}
          data-testid="client-error"
        >
          <HStack
            justify="space-between"
            align="flex-start"
            flexWrap="wrap"
            spacing={2}
          >
            <Box minW={0}>
              <HStack spacing={2} mb={1}>
                <Badge
                  colorScheme="purple"
                  borderRadius="full"
                  px={2}
                  textTransform="none"
                >
                  {group.platform}
                </Badge>
                <Text fontSize="xs" color="muted">
                  {group.appVersion}
                  {group.route ? ` · ${group.route}` : ''}
                </Text>
              </HStack>
              <Text fontSize="sm" wordBreak="break-word">
                {group.message}
              </Text>
            </Box>
            <HStack spacing={2} flexShrink={0}>
              <Badge borderRadius="full" px={2} textTransform="none">
                {group.count}×
              </Badge>
              <Text fontSize="xs" color="muted" title={group.lastSeen}>
                {ago(group.lastSeen)}
              </Text>
            </HStack>
          </HStack>
          <Box mt={1}>
            <Stack stack={group.stack} />
          </Box>
        </Box>
      ))}
    </VStack>
  );
}

function Alerts({ alerts }: { alerts: AlertState[] }) {
  return (
    <Box overflowX="auto">
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Threshold</Th>
            <Th>State</Th>
            <Th display={{ base: 'none', md: 'table-cell' }}>Detail</Th>
            <Th>Last emailed</Th>
          </Tr>
        </Thead>
        <Tbody>
          {alerts.map((alert) => (
            <Tr key={alert.threshold} data-testid="alert-state">
              <Td fontSize="sm">
                {ALERT_THRESHOLD_LABELS[alert.threshold] ?? alert.threshold}
              </Td>
              <Td>
                <OkBadge
                  ok={!alert.tripped}
                  label={alert.tripped ? 'tripped' : 'ok'}
                />
                {alert.tripped && alert.since && (
                  <Text fontSize="xs" color="muted" mt={1}>
                    since {ago(alert.since)}
                  </Text>
                )}
              </Td>
              <Td
                display={{ base: 'none', md: 'table-cell' }}
                fontSize="xs"
                color="muted"
                maxW="420px"
              >
                {alert.detail ?? '—'}
              </Td>
              <Td
                fontSize="xs"
                color="muted"
                whiteSpace="nowrap"
                title={alert.lastEmailedAt ?? ''}
              >
                {alert.lastEmailedAt ? ago(alert.lastEmailedAt) : 'never'}
                {alert.lastRecoveredAt && !alert.tripped && (
                  <Text as="span" display="block">
                    recovered {ago(alert.lastRecoveredAt)}
                  </Text>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

export function HealthTab() {
  const toast = useToast();
  const [range, setRange] = useState<HealthWindow>('24h');
  // A slow answer to an earlier window never overwrites a later one.
  const requestNo = useRef(0);
  const [overview, setOverview] = useState<HealthOverview | null>(null);
  const [errors, setErrors] = useState<HealthErrors | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (w: HealthWindow) => {
      const no = ++requestNo.current;
      setLoading(true);
      try {
        const [o, e] = await Promise.all([
          adminApi.getHealthOverview(w),
          adminApi.getHealthErrors(w),
        ]);
        if (no !== requestNo.current) return;
        setOverview(o.data);
        setErrors(e.data);
      } catch (err) {
        if (no !== requestNo.current) return;
        toast({
          title: 'Could not load health',
          description:
            err instanceof ApiError ? err.message : 'Something went wrong',
          status: 'error',
        });
      } finally {
        if (no === requestNo.current) setLoading(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    void load(range);
  }, [load, range]);

  const tripped = overview?.alerts.filter((alert) => alert.tripped) ?? [];
  const p = overview?.push.transports;
  const pushRate = (transport: { sent: number; failed: number }) =>
    transport.sent === 0
      ? '—'
      : pct((transport.sent - transport.failed) / transport.sent, 0);

  return (
    <VStack align="stretch" spacing={6}>
      <HStack justify="space-between" flexWrap="wrap" spacing={3}>
        <Text color="muted" fontSize="sm" maxW="640px">
          What the server knows about itself: the canary every 5 minutes,
          requests and errors, push, the scheduler and sweeps, and the alert
          thresholds. Error groups carry route patterns and scrubbed messages,
          never who was affected.
        </Text>
        <HStack spacing={2}>
          <ButtonGroup
            size="sm"
            isAttached
            variant="outline"
            colorScheme="gray"
          >
            {(['24h', '7d'] as HealthWindow[]).map((w) => (
              <Button
                key={w}
                onClick={() => setRange(w)}
                isActive={range === w}
                borderColor="line"
                aria-pressed={range === w}
              >
                {WINDOW_LABELS[w]}
              </Button>
            ))}
          </ButtonGroup>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            borderColor="line"
            leftIcon={<RefreshCw size={14} />}
            onClick={() => void load(range)}
            isLoading={loading}
          >
            Refresh
          </Button>
        </HStack>
      </HStack>

      {loading && !overview && <Spinner size="sm" />}

      {overview && (
        <>
          {tripped.length > 0 && (
            <Box
              bg="accent.tint"
              borderWidth="1px"
              borderColor="accent"
              borderRadius="card"
              px={4}
              py={3}
              data-testid="tripped-banner"
            >
              <Text fontWeight={700} color="accent" fontSize="sm">
                {tripped.length === 1
                  ? '1 threshold tripped'
                  : `${tripped.length} thresholds tripped`}
                :{' '}
                {tripped
                  .map((alert) => ALERT_THRESHOLD_LABELS[alert.threshold])
                  .join(', ')}
              </Text>
            </Box>
          )}

          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
            <Card
              label="Uptime (canary)"
              value={pct(overview.canary.passRate)}
              note={`${overview.canary.runs} runs, ${overview.canary.failed} with a failure · last ${ago(overview.canary.lastRunAt)}`}
              tone={
                overview.canary.passRate === null
                  ? 'neutral'
                  : overview.canary.passRate >= 0.99
                    ? 'good'
                    : 'warn'
              }
              testId="card-uptime"
            />
            <Card
              label="5xx rate"
              value={pct(overview.requests.errorRate, 2)}
              note={`${overview.requests.errors} of ${overview.requests.total} requests`}
              tone={
                overview.requests.errorRate === null
                  ? 'neutral'
                  : overview.requests.errorRate > 0.01
                    ? 'bad'
                    : 'good'
              }
              testId="card-errors"
            />
            <Card
              label="Live sockets"
              value={String(overview.websockets.connected)}
              note="signed-in connections right now"
            />
            <Card
              label="Scheduler"
              value={
                overview.scheduler.lagMs > 0
                  ? `${Math.round(overview.scheduler.lagMs / 1000)} s late`
                  : 'on time'
              }
              note={`${overview.scheduler.due} due · last tick ${ago(overview.scheduler.lastTickAt)}`}
              tone={overview.scheduler.lagMs > 5 * 60_000 ? 'bad' : 'neutral'}
            />
            <Card
              label="Push (last hour)"
              value={
                p && p.vapid.sent + p.apns.sent + p.fcm.sent > 0
                  ? pct(
                      1 -
                        (p.vapid.failed + p.apns.failed + p.fcm.failed) /
                          (p.vapid.sent + p.apns.sent + p.fcm.sent),
                      0
                    )
                  : '—'
              }
              note={
                p
                  ? `web ${pushRate(p.vapid)} (${p.vapid.sent}) · iOS ${pushRate(p.apns)} (${p.apns.sent}) · Android ${pushRate(p.fcm)} (${p.fcm.sent})`
                  : undefined
              }
              testId="card-push"
            >
              <PushFailures
                failures={overview.push.failures ?? []}
                configProblems={overview.push.configProblems ?? []}
              />
            </Card>
            <Card
              label="Storage"
              value={
                overview.storage.ok === null
                  ? 'not checked'
                  : overview.storage.ok
                    ? 'ok'
                    : 'failed'
              }
              note={`${overview.storage.driver}${overview.storage.reason ? ` · ${overview.storage.reason}` : ''} · ${ago(overview.storage.checkedAt)}`}
              tone={
                overview.storage.ok === null
                  ? 'neutral'
                  : overview.storage.ok
                    ? 'good'
                    : 'bad'
              }
              testId="card-storage"
            />
            <Card
              label="Database pool"
              value={
                overview.dbPool
                  ? `${overview.dbPool.serverConnections ?? '?'} / ${overview.dbPool.max}`
                  : '—'
              }
              note="server connections / pool size"
            />
            <Card
              label="Build"
              value={overview.build.shortCommit ?? 'unknown'}
              note={`up ${duration(overview.build.uptimeSeconds)} · since ${when(overview.build.startedAt)}`}
              testId="card-build"
            />
          </SimpleGrid>

          <Section title="Latest canary run">
            <CanaryChecks latest={overview.canary.latest} />
          </Section>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Section title="p95 latency by route group">
              {overview.requests.groups.length === 0 ? (
                <Text color="muted">No requests recorded yet.</Text>
              ) : (
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Group</Th>
                      <Th isNumeric>Requests</Th>
                      <Th isNumeric>5xx</Th>
                      <Th isNumeric>p95</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {overview.requests.groups.map((group) => (
                      <Tr key={group.group} data-testid="route-group">
                        <Td fontFamily="mono" fontSize="xs">
                          {group.group}
                        </Td>
                        <Td isNumeric fontSize="sm">
                          {group.count}
                        </Td>
                        <Td
                          isNumeric
                          fontSize="sm"
                          color={group.errors ? 'red.600' : undefined}
                        >
                          {group.errors}
                        </Td>
                        <Td isNumeric fontSize="sm">
                          {group.p95Ms} ms
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </Section>
            <Section title="Sweeps">
              <Sweeps sweeps={overview.sweeps} />
            </Section>
          </SimpleGrid>

          <Section title="Alerts">
            <Text fontSize="xs" color="muted" mb={2}>
              {overview.email.configured
                ? `Emailing via ${overview.email.via}${overview.email.dailyDigest ? ', with a daily digest' : ''}; one email per threshold per hour, a recovery email when it clears.`
                : 'Email alerts are off: set ALERT_EMAIL_TO and SMTP_URL or RESEND_API_KEY. Thresholds are still evaluated and shown here.'}
            </Text>
            <Alerts alerts={overview.alerts} />
          </Section>
        </>
      )}

      {errors && (
        <>
          <Section title={`Server errors (${errors.server.length} groups)`}>
            <ServerErrors groups={errors.server} />
          </Section>
          <Section title={`App errors (${errors.client.length} groups)`}>
            <ClientErrors groups={errors.client} />
          </Section>
          <Section title="App failure counters">
            {errors.counters.length === 0 ? (
              <Text color="muted">Nothing counted in this window.</Text>
            ) : (
              <HStack spacing={3} flexWrap="wrap">
                {errors.counters.map((counter) => (
                  <Badge
                    key={`${counter.platform}-${counter.name}`}
                    borderRadius="full"
                    px={3}
                    py={1}
                    textTransform="none"
                    data-testid="counter"
                  >
                    {counter.platform} · {counter.name.replace(/_/g, ' ')}:{' '}
                    {counter.count}
                  </Badge>
                ))}
              </HStack>
            )}
          </Section>
        </>
      )}
    </VStack>
  );
}
